import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Apple, PlayCircle, Smartphone, Clock, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Per-platform live flags.
const ANDROID_LIVE = true;
const IOS_LIVE = true;

const APP_STORE_URL = "https://apps.apple.com/us/app/getarvo/id6762120842";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=nz.getarvo.app";

// Both platforms live → QR points to a smart-redirect landing page that picks
// the right store based on the user's device UA.
const QR_TARGET_URL = ANDROID_LIVE && IOS_LIVE
  ? "https://getarvo.co.nz/download.html"
  : ANDROID_LIVE
    ? PLAY_STORE_URL
    : IOS_LIVE
      ? APP_STORE_URL
      : "https://getarvo.co.nz/#download";

const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=2&data=${encodeURIComponent(
  QR_TARGET_URL,
)}`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EarlyAccessForm() {
  const [form, setForm] = useState({ email: "", name: "", farm_type: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("We just need your email to get you on the list.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, {
        email: form.email,
        name: form.name || null,
        farm_type: form.farm_type || null,
        source: "landing-download",
      });
      toast.success("You're on the list.");
      setDone(true);
      setForm({ email: "", name: "", farm_type: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your email.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="early-access-success"
        className="rounded-md border border-[#2D4A22] bg-[#F9F8F6] p-6"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-[#2D4A22] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-display font-bold text-lg text-[#2D4A22]">
              You're on the iOS list.
            </p>
            <p className="mt-1 text-sm text-[#4A4F4A]">
              We'll email you the moment Arvo lands on the App Store. In the
              meantime, Android users can install it from Google Play right now.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="early-access-reset"
          onClick={() => setDone(false)}
          className="mt-4 text-sm font-semibold text-[#D96C32] hover:underline"
        >
          Add another →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="early-access-form">
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <Input
          data-testid="early-access-email"
          type="email"
          placeholder="[email protected]"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="sm:col-span-3 h-12 border-[#D5D1C8] focus-visible:ring-[#2D4A22]"
          required
        />
        <Select
          value={form.farm_type}
          onValueChange={(v) => setForm({ ...form, farm_type: v })}
        >
          <SelectTrigger
            data-testid="early-access-farm-type"
            className="sm:col-span-2 h-12 border-[#D5D1C8] focus:ring-[#2D4A22]"
          >
            <SelectValue placeholder="Farm type (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Dairy">Dairy</SelectItem>
            <SelectItem value="Beef">Beef</SelectItem>
            <SelectItem value="Contractor">Contractor</SelectItem>
            <SelectItem value="Service Provider">Service Provider</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button
        type="submit"
        data-testid="early-access-submit"
        disabled={loading}
        className="w-full bg-[#D96C32] hover:bg-[#B85A28] text-white font-bold py-4 h-auto rounded-md shadow-sm hover:-translate-y-0.5 transition-all"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Adding you…
          </>
        ) : (
          <>
            Notify me when iOS launches
            <ArrowRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
      <p className="text-xs text-[#4A4F4A]/80">
        One email when iOS goes live. No spam, no passing your details on — ever.
      </p>
    </form>
  );
}

function StoreBadge({ icon: Icon, line1, line2, live, href, testid }) {
  const content = (
    <>
      <Icon className="w-7 h-7" strokeWidth={1.6} />
      <div className="text-left leading-tight">
        <p className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">
          {line1}
        </p>
        <p className="font-display font-bold text-lg">{line2}</p>
      </div>
      {!live && (
        <span className="ml-auto text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#F2C76A] text-[#1A1D1A]">
          Soon
        </span>
      )}
    </>
  );

  const base =
    "flex-1 inline-flex items-center gap-3 rounded-md px-5 py-3 transition-colors";

  if (live) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className={`${base} bg-[#1A1D1A] hover:bg-[#2D4A22] text-white`}
      >
        {content}
      </a>
    );
  }
  return (
    <div
      data-testid={testid}
      aria-disabled="true"
      className={`${base} bg-[#1A1D1A]/60 text-white cursor-default select-none`}
    >
      {content}
    </div>
  );
}

export default function DownloadSection() {
  return (
    <section
      id="download"
      data-testid="download-section"
      className="bg-[#F9F8F6] py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: copy + early access */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#2D4A22]/10 border border-[#2D4A22]/30 px-3.5 py-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#2D4A22]" strokeWidth={2.5} />
              <span className="text-xs font-bold tracking-[0.18em] uppercase text-[#2D4A22]">
                Live on Google Play & App Store
              </span>
            </div>

            <h2
              data-testid="download-headline"
              className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#1A1D1A] leading-tight"
            >
              Get Arvo on your phone today.
            </h2>
            <p className="mt-5 text-lg text-[#4A4F4A] leading-relaxed max-w-xl">
              Arvo is live on both Google Play and the App Store. Tap the badge
              for your phone, or scan the QR code with your camera to install
              in seconds.
            </p>

            {/* Store badges — both stores live */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
              <StoreBadge
                icon={Apple}
                line1="Download on the"
                line2="App Store"
                live={IOS_LIVE}
                href={APP_STORE_URL}
                testid="download-app-store"
              />
              <StoreBadge
                icon={PlayCircle}
                line1="Get it on"
                line2="Google Play"
                live={ANDROID_LIVE}
                href={PLAY_STORE_URL}
                testid="download-play-store"
              />
            </div>
          </div>

          {/* Right: QR code → smart redirect (hidden on small screens) */}
          <div className="hidden sm:block lg:col-span-5">
            <div
              data-testid="download-qr-card"
              className="relative bg-white border border-[#D5D1C8] rounded-md p-7 shadow-sm max-w-sm mx-auto"
            >
              <div className="flex items-center gap-2 mb-4">
                <Smartphone className="w-5 h-5 text-[#2D4A22]" />
                <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#2D4A22]">
                  Scan to install Arvo
                </p>
              </div>

              <div className="relative rounded-md overflow-hidden border border-[#D5D1C8] bg-[#F9F8F6] p-4">
                <img
                  src={QR_URL}
                  alt="QR code to install Arvo from the App Store or Google Play"
                  loading="lazy"
                  className="w-full h-auto block"
                />
              </div>

              <div className="mt-5 flex items-start gap-3">
                <span className="inline-flex w-7 h-7 flex-shrink-0 items-center justify-center rounded-full bg-[#2D4A22] text-white text-xs font-bold">
                  1
                </span>
                <p className="text-sm text-[#4A4F4A] leading-relaxed">
                  Open your phone's camera and point it at the code.
                </p>
              </div>
              <div className="mt-3 flex items-start gap-3">
                <span className="inline-flex w-7 h-7 flex-shrink-0 items-center justify-center rounded-full bg-[#2D4A22] text-white text-xs font-bold">
                  2
                </span>
                <p className="text-sm text-[#4A4F4A] leading-relaxed">
                  Tap the link — your iPhone heads to the App Store, your
                  Android heads to Google Play.
                </p>
              </div>

              <a
                href={QR_TARGET_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-smartlink"
                className="mt-5 block text-center text-xs font-mono text-[#4A4F4A] hover:text-[#2D4A22] underline-offset-4 hover:underline truncate"
              >
                getarvo.co.nz/download.html
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
